import React, { useEffect, useState } from "react";
import { Badge, OverlayTrigger, Tooltip, Dropdown } from "react-bootstrap";
import moment from "moment";
import { getContactNames } from "../../Api/TicketApiOrg";
import "./ProjectTasksTab.css";
import { softDeleteStage } from "../../Api/StageMaster";
import { toast } from "react-toastify";

const ProjectTasksTab = ({
  // Data
  stages,
  filteredStageTasks,
  tasksLoading,
  tasksLoadingByStage,
  taskAssignmentsByTask,
  teamMemberDetails,
  resourcesByOrg,
  clientContactPersons,
  partnerContactPersons,
  contactNamesCache,
  setContactNamesCache,
  ticketNameMap,
  eventNameMap,
  taskBuildingBlocks,
  taskTypesLoading,
  taskStatusesLoading,
  handleBatchCreateClick,

  // Permissions
  hasEditPermission,

  // Handlers
  handleOpenTaskDetails,
  handleEditTask,
  handleSoftDeleteTask,
  openTaskDialog,
  getCurrentAssignedUsersForTask,
  getTaskTypeName,
  getTaskStatusName,
  getPriorityBadge,
  getStatusBadge,
  handleEditStage,
  handleCreateStage,
  setShowTaskOptions,
  showTaskOptions,
  handleExcelUploadClick,
  handleManualTaskClick,
  exportTasksToExcel,
  searchQuery,
  setSearchQuery,
  projectId,
  onStageDeleted,
}) => {
  // State for hover tooltip
  const [hoveredAssignee, setHoveredAssignee] = useState(null);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  // Filter state - Changed to arrays for multi-select
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [uniqueAssignees, setUniqueAssignees] = useState([]);
  const [uniqueStatuses, setUniqueStatuses] = useState([]);

  // Add this with other handlers in ProjectTasksTab
  const handleDeleteStage = async (stage) => {
  if (!hasEditPermission) {
    toast.error("You don't have permission to delete stages");
    return;
  }

  // First check if stage has any tasks
  const stageTasksCount = filteredStageTasks[stage.stageId]?.length || 0;

  if (stageTasksCount > 0) {
    toast.error(
      `Cannot delete stage "${stage.stageName}" because it has ${stageTasksCount} task(s). Please reassign or delete the tasks first.`,
    );
    return;
  }

  const confirmed = window.confirm(
    `Are you sure you want to delete stage "${stage.stageName}"? This action can be undone later.`,
  );

  if (!confirmed) return;

  const reason = window.prompt(
    "Please provide a reason for deleting this stage (optional):",
    "",
  );

  try {
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const userId = userDetails?.userId || "";

    const result = await softDeleteStage(stage.stageId, userId, reason || "");

    // FIX: Check for both lowercase and uppercase message properties
    const successMessage = result.message || result.Message;
    
    if (successMessage) {
      toast.success(successMessage);
      // Call the refresh function passed from parent
      if (onStageDeleted) {
        onStageDeleted();
      }
    } else {
      toast.error("Failed to delete stage");
    }
  } catch (error) {
    console.error("Error deleting stage:", error);
    if (
      error.Message &&
      error.Message.includes("has associated active tasks")
    ) {
      toast.error(error.Message);
    } else {
      toast.error(error.message || "Failed to delete stage");
    }
  }
};

  // Function to fetch contact names for GUIDs
  const fetchContactNames = async (guids) => {
    if (!guids || guids.length === 0) return;

    const validGuids = guids.filter(
      (g) =>
        g &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          g,
        ),
    );

    if (validGuids.length === 0) return;

    try {
      const res = await getContactNames(validGuids);
      const map = {};
      res.forEach((c) => {
        map[c.contactGuid.toLowerCase()] = c.contactName;
      });
      setContactNamesCache((prev) => ({
        ...prev,
        ...map,
      }));
    } catch (err) {
      console.error("Failed to fetch contact names", err);
    }
  };

  // Collect all GUIDs from assignments and fetch their names
  useEffect(() => {
    const allGuids = new Set();

    const allTasks = Object.values(filteredStageTasks).flat();

    allTasks.forEach((task) => {
      const assignments = taskAssignmentsByTask[task.taskId] || [];
      assignments.forEach((assignment) => {
        const userId = assignment.assignedUserId;
        if (
          userId &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            userId,
          )
        ) {
          if (!contactNamesCache[userId.toLowerCase()]) {
            allGuids.add(userId);
          }
        }
      });
    });

    if (allGuids.size > 0) {
      fetchContactNames(Array.from(allGuids));
    }
  }, [filteredStageTasks, taskAssignmentsByTask]);

  // Extract unique assignees from all tasks
  useEffect(() => {
    const allTasks = Object.values(filteredStageTasks).flat();
    const assigneeMap = new Map();

    allTasks.forEach((task) => {
      const assignments = taskAssignmentsByTask[task.taskId] || [];
      assignments.forEach((assignment) => {
        const userId = assignment.assignedUserId;
        if (userId) {
          const assigneeName = getAssigneeName(userId);
          if (!assigneeMap.has(userId)) {
            assigneeMap.set(userId, {
              userId: userId,
              name: assigneeName,
              avatarColor: getAvatarColor(userId),
              initials: getInitials(assigneeName),
            });
          }
        }
      });
    });

    // Add "Unassigned" filter option
    const hasUnassignedTasks = allTasks.some((task) => {
      const assignments = taskAssignmentsByTask[task.taskId] || [];
      return assignments.length === 0;
    });

    const assigneeList = Array.from(assigneeMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );

    if (hasUnassignedTasks) {
      assigneeList.unshift({
        userId: "unassigned",
        name: "Unassigned",
        avatarColor: "#94a3b8",
        initials: "??",
      });
    }

    setUniqueAssignees(assigneeList);
  }, [filteredStageTasks, taskAssignmentsByTask, contactNamesCache]);

  // Extract unique statuses from all tasks
  useEffect(() => {
    const allTasks = Object.values(filteredStageTasks).flat();
    const statusMap = new Map();

    allTasks.forEach((task) => {
      if (task.taskStatus) {
        const statusName = getTaskStatusName(task.taskStatus);
        if (!statusMap.has(task.taskStatus)) {
          statusMap.set(task.taskStatus, {
            value: task.taskStatus,
            label: statusName,
          });
        }
      }
    });

    const statusOrder = [
      "Not Started",
      "In Progress",
      "On Hold",
      "Completed",
      "Cancelled",
    ];

    const statusList = Array.from(statusMap.values()).sort((a, b) => {
      const indexA = statusOrder.indexOf(a.label);
      const indexB = statusOrder.indexOf(b.label);

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.label.localeCompare(b.label);
    });

    setUniqueStatuses(statusList);
  }, [filteredStageTasks, getTaskStatusName]);

  // Helper function to sort tasks
  const sortTasks = (tasks) => {
    if (!sortConfig.key || !tasks) return tasks;

    const sortedTasks = [...tasks];

    sortedTasks.sort((a, b) => {
      let aValue, bValue;

      if (sortConfig.key === "taskId") {
        aValue = extractTaskIdNumber(a.taskId);
        bValue = extractTaskIdNumber(b.taskId);
      } else if (sortConfig.key === "endDate") {
        aValue = a.taskEndDate ? new Date(a.taskEndDate).getTime() : null;
        bValue = b.taskEndDate ? new Date(b.taskEndDate).getTime() : null;

        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return 1;
        if (bValue === null) return -1;
      } else if (sortConfig.key === "status") {
        aValue = getTaskStatusName(a.taskStatus);
        bValue = getTaskStatusName(b.taskStatus);
      } else {
        return 0;
      }

      if (sortConfig.direction === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return sortedTasks;
  };

  // Helper function to filter tasks with multi-select
  const filterTasks = (tasks) => {
    let filtered = tasks;

    // Filter by assignees (multi-select)
    if (selectedAssignees && selectedAssignees.length > 0) {
      filtered = filtered.filter((task) => {
        const assignments = taskAssignmentsByTask[task.taskId] || [];

        // Check if unassigned is selected
        const isUnassignedSelected = selectedAssignees.includes("unassigned");

        if (isUnassignedSelected && assignments.length === 0) {
          return true;
        }

        // Check if task has any assignment that matches selected assignees
        return assignments.some((assignment) =>
          selectedAssignees.includes(assignment.assignedUserId),
        );
      });
    }

    // Filter by statuses (multi-select)
    if (selectedStatuses && selectedStatuses.length > 0) {
      filtered = filtered.filter((task) => {
        return selectedStatuses.includes(task.taskStatus);
      });
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter((task) =>
        task.taskName?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    return filtered;
  };

  // Helper function to extract numeric value from Task ID
  const extractTaskIdNumber = (taskId) => {
    if (!taskId) return 0;
    const match = taskId.match(/\d+$/);
    if (match) {
      return parseInt(match[0], 10);
    }
    return taskId;
  };

  // Handle sort click
  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <i
          className="fas fa-sort ms-1"
          style={{ fontSize: "12px", opacity: 0.5 }}
        ></i>
      );
    }
    return sortConfig.direction === "asc" ? (
      <i className="fas fa-sort-up ms-1" style={{ fontSize: "12px" }}></i>
    ) : (
      <i className="fas fa-sort-down ms-1" style={{ fontSize: "12px" }}></i>
    );
  };

  // Toggle assignee selection
  const toggleAssignee = (assigneeId) => {
    setSelectedAssignees((prev) => {
      if (prev.includes(assigneeId)) {
        return prev.filter((id) => id !== assigneeId);
      } else {
        return [...prev, assigneeId];
      }
    });
  };

  // Toggle status selection
  const toggleStatus = (statusValue) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(statusValue)) {
        return prev.filter((s) => s !== statusValue);
      } else {
        return [...prev, statusValue];
      }
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedAssignees([]);
    setSelectedStatuses([]);
    setSearchQuery("");
  };

  // Helper function to get ALL assignments for a task
  const getAllAssignmentsForTask = (taskId) => {
    return taskAssignmentsByTask[taskId] || [];
  };

  // Helper function to get assignee name
  const getAssigneeName = (userId) => {
    if (!userId) return "Unknown";

    const contactName = contactNamesCache?.[userId?.toLowerCase()];
    if (contactName) return contactName;

    const teamMember = teamMemberDetails?.find(
      (user) => String(user.userId) === String(userId),
    );
    if (teamMember) return teamMember.username;

    const clientContact = clientContactPersons?.find(
      (c) => c.cusVenId === userId || c.contactGuid === userId,
    );
    if (clientContact)
      return clientContact.contactPerName || clientContact.contactName;

    const partnerContact = partnerContactPersons?.find(
      (c) => c.cusVenId === userId || c.contactGuid === userId,
    );
    if (partnerContact)
      return partnerContact.contactPerName || partnerContact.contactName;

    const resource = resourcesByOrg?.find(
      (res) => String(res.resourceId) === String(userId),
    );
    if (resource) return resource.name;

    const isGuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        userId,
      );
    if (isGuid) return userId.substring(0, 8);

    return userId;
  };

  // Helper function to get initials
  const getInitials = (name) => {
    if (!name || name === "Unknown") return "??";

    const cleanName = name.trim();

    if (cleanName.length === 2) return cleanName.toUpperCase();

    const words = cleanName.split(/\s+/);
    if (words.length >= 2) {
      const firstInitial = words[0][0]?.toUpperCase() || "";
      const lastInitial = words[words.length - 1][0]?.toUpperCase() || "";
      return firstInitial + lastInitial;
    }

    if (cleanName.length >= 2) {
      return cleanName.substring(0, 2).toUpperCase();
    }

    if (cleanName.length === 1) {
      return cleanName[0].toUpperCase() + "?";
    }

    return "??";
  };

  // Helper function to get avatar color
  const getAvatarColor = (userId) => {
    const colors = [
      "#6366f1",
      "#8b5cf6",
      "#ec4899",
      "#f43f5e",
      "#ef4444",
      "#f97316",
      "#f59e0b",
      "#eab308",
      "#84cc16",
      "#10b981",
      "#14b8a6",
      "#06b6d4",
      "#3b82f6",
      "#6b7280",
    ];
    let hash = 0;
    const str = String(userId);
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Helper to get priority display
  const getPriorityDisplay = (priority) => {
    if (priority === "Critical" || priority === "critical") {
      return (
        <div className="ptt-priority-critical">
          <i className="fas fa-exclamation-triangle"></i>
          <span>Critical</span>
        </div>
      );
    }
    if (priority === "High" || priority === "high") {
      return (
        <div className="ptt-priority-high">
          <i className="fas fa-flag"></i>
          <span>High</span>
        </div>
      );
    }
    if (priority === "Medium" || priority === "medium") {
      return (
        <div className="ptt-priority-medium">
          <i className="fas fa-flag"></i>
          <span>Medium</span>
        </div>
      );
    }
    if (priority === "Low" || priority === "low") {
      return (
        <div className="ptt-priority-low">
          <i className="fas fa-flag"></i>
          <span>Low</span>
        </div>
      );
    }
    return (
      <div className="ptt-priority-default">
        <i className="fas fa-minus-circle"></i>
        <span>{priority || "No Priority"}</span>
      </div>
    );
  };

  // Helper to get status display
  const getStatusDisplay = (status) => {
    const statusName = getTaskStatusName(status);
    if (statusName === "Completed") {
      return <span className="ptt-status-completed">{statusName}</span>;
    }
    if (statusName === "In Progress" || statusName === "Active") {
      return <span className="ptt-status-active">{statusName}</span>;
    }
    if (statusName === "On Hold") {
      return <span className="ptt-status-onhold">{statusName}</span>;
    }
    if (statusName === "Cancelled") {
      return <span className="ptt-status-cancelled">{statusName}</span>;
    }
    return <span className="ptt-status-pending">{statusName}</span>;
  };

  // Helper to check if there are any tasks at all
  const hasAnyTasks = () => {
    return Object.values(filteredStageTasks).some(
      (tasks) => tasks && tasks.length > 0,
    );
  };

  // Check if any filter is active
  const isFilterActive = () => {
    return (
      selectedAssignees.length > 0 ||
      selectedStatuses.length > 0 ||
      searchQuery !== ""
    );
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedAssignees.length > 0) count += selectedAssignees.length;
    if (selectedStatuses.length > 0) count += selectedStatuses.length;
    if (searchQuery) count++;
    return count;
  };

  const noStageTasks = filteredStageTasks["no-stage"] || [];

  // Render tasks table component
  const renderTaskTable = (tasks, showStageColumn = false) => {
    const filteredTasks = filterTasks(tasks);
    const sortedTasks = sortTasks(filteredTasks);

    return (
      <div className="table-responsive">
        <table className="ptt-task-table">
          <thead>
            <tr>
              <th
                style={{ width: "7%", cursor: "pointer" }}
                onClick={() => handleSort("taskId")}
              >
                TaskId {getSortIcon("taskId")}
              </th>
              <th className="ptt-col-name">Name</th>
              <th className="ptt-col-assignee">Assignee</th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("endDate")}
              >
                End Date {getSortIcon("endDate")}
              </th>
              <th className="ptt-col-priority">Priority</th>
              <th
                className="ptt-col-status"
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("status")}
              >
                Status {getSortIcon("status")}
              </th>
              <th className="ptt-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedTasks.map((task) => {
              const allAssignments = getAllAssignmentsForTask(task.taskId);

              return (
                <tr key={task.taskId} className="ptt-task-row">
                  <td className="ptt-task-id-cell">{task.taskId}</td>

                  <td className="ptt-col-name">
                    <div className="ptt-task-name">
                      <i className="fas fa-play ptt-play-icon"></i>
                      <i className="fas fa-check-circle ptt-check-icon"></i>
                      <a
                        href={`/project/${task.projectBelong || projectId}/task/${task.taskId}`}
                        className="ptt-task-name-text"
                        onClick={(e) => {
                          if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                            e.preventDefault();
                            handleOpenTaskDetails(task.taskId);
                          }
                        }}
                        title="Click to view task details (Ctrl+Click to open in new tab)"
                      >
                        {task.taskName}
                      </a>
                    </div>
                  </td>

                  <td className="ptt-col-assignee">
                    {allAssignments.length === 0 ? (
                      <div className="d-flex align-items-center gap-2">
                        <span className="ptt-unassigned-text">Unassigned</span>
                        {hasEditPermission && (
                          <button
                            className="ptt-assign-btn"
                            onClick={() => openTaskDialog(task)}
                            title="Manage Assignments"
                          >
                            <i className="fas fa-user-plus"></i>
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="ptt-assignee-avatars-container">
                        {allAssignments.map((assignment, idx) => {
                          const assigneeName = getAssigneeName(
                            assignment.assignedUserId,
                          );
                          const initials = getInitials(assigneeName);
                          const avatarColor = getAvatarColor(
                            assignment.assignedUserId,
                          );

                          return (
                            <OverlayTrigger
                              key={assignment.assignmentId || idx}
                              placement="top"
                              overlay={
                                <Tooltip
                                  id={`tooltip-${assignment.assignmentId || idx}`}
                                >
                                  <div className="text-center">
                                    <strong>{assigneeName}</strong>
                                  </div>
                                </Tooltip>
                              }
                            >
                              <div
                                className="ptt-assignee-avatar"
                                style={{ backgroundColor: avatarColor }}
                                title={assigneeName}
                              >
                                {initials}
                              </div>
                            </OverlayTrigger>
                          );
                        })}
                        {hasEditPermission && (
                          <button
                            className="ptt-assign-btn-icon"
                            onClick={() => openTaskDialog(task)}
                            title="Manage Assignments"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                        )}
                      </div>
                    )}
                  </td>

                  <td className="ptt-date-cell">
                    {task.taskEndDate
                      ? moment(task.taskEndDate).format("MMM DD, YYYY")
                      : "-"}
                  </td>

                  <td className="text-center">
                    <span className="ptt-priority-badge">
                      {task.taskPriority || "No Priority"}
                    </span>
                  </td>

                  <td className="text-center">
                    {getStatusDisplay(task.taskStatus)}
                  </td>

                  <td className="ptt-actions-cell">
                    {hasEditPermission && (
                      <button
                        className="ptt-delete-task-btn"
                        onClick={() => handleSoftDeleteTask(task.taskId)}
                        title="Delete Task"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {sortedTasks.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center py-3 text-muted">
                  No tasks found matching the filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="ptt-tasks-container">
      <div className="ptt-task-list-container">
        {/* Controls Bar */}
        <div className="ptt-controls-bar">
          <div className="ptt-left-controls">
            {hasEditPermission && (
              <>
                <div className="ptt-dropdown">
                  <button
                    className="ptt-btn-primary"
                    onClick={() => setShowTaskOptions(!showTaskOptions)}
                  >
                    Add Task
                  </button>
                  {showTaskOptions && (
                    <>
                      <div
                        className="ptt-overlay"
                        onClick={() => setShowTaskOptions(false)}
                      />
                      <div className="ptt-dropdown-menu">
                        <div
                          className="ptt-dropdown-item"
                          onClick={handleBatchCreateClick}
                        >
                          <div>
                            <span>Bulk Create Tasks</span>
                            <small
                              className="text-muted d-block"
                              style={{ fontSize: "10px" }}
                            >
                              Create multiple tasks at once
                            </small>
                          </div>
                        </div>

                        <div
                          className="ptt-dropdown-item"
                          onClick={handleExcelUploadClick}
                        >
                          <div>
                            <span>Import from Excel</span>
                            <small
                              className="text-muted d-block"
                              style={{ fontSize: "10px" }}
                            >
                              Upload Excel file with tasks
                            </small>
                          </div>
                        </div>

                        <div
                          className="ptt-dropdown-item"
                          onClick={handleManualTaskClick}
                        >
                          <div>
                            <span>Create Single Task</span>
                            <small
                              className="text-muted d-block"
                              style={{ fontSize: "10px" }}
                            >
                              Add one task manually
                            </small>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <button className="ptt-btn-primary" onClick={handleCreateStage}>
                  Add Stage
                </button>
              </>
            )}
          </div>

          <div className="ptt-right-controls">
            {/* Status Filter Dropdown - Multi-Select */}
            {uniqueStatuses.length > 0 && (
              <Dropdown className="ptt-status-filter-dropdown">
                <Dropdown.Toggle
                  variant="outline-secondary"
                  className="ptt-filter-toggle"
                >
                  <i className="fas fa-tasks me-1"></i>
                  {selectedStatuses.length === 0
                    ? "All Statuses"
                    : `${selectedStatuses.length} status${selectedStatuses.length > 1 ? "es" : ""} selected`}
                  {selectedStatuses.length > 0 && (
                    <Badge
                      bg="primary"
                      className="ms-2"
                      style={{ fontSize: "10px" }}
                    >
                      {selectedStatuses.length}
                    </Badge>
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu className="ptt-filter-menu ptt-multi-select-menu">
                  <Dropdown.Item
                    onClick={() => setSelectedStatuses([])}
                    className="ptt-select-all-item"
                  >
                    <i className="fas fa-times-circle me-2"></i> Clear All
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  {uniqueStatuses.map((status) => (
                    <Dropdown.Item
                      key={status.value}
                      onClick={() => toggleStatus(status.value)}
                      active={selectedStatuses.includes(status.value)}
                      className="ptt-multi-select-item"
                    >
                      <div className="d-flex align-items-center w-100">
                        <input
                          type="checkbox"
                          checked={selectedStatuses.includes(status.value)}
                          onChange={() => {}}
                          className="me-2"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span
                          className="status-dot"
                          style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            display: "inline-block",
                            marginRight: "8px",
                            backgroundColor:
                              status.label === "Completed"
                                ? "#10b981"
                                : status.label === "In Progress"
                                  ? "#3b82f6"
                                  : status.label === "On Hold"
                                    ? "#f59e0b"
                                    : status.label === "Cancelled"
                                      ? "#ef4444"
                                      : "#94a3b8",
                          }}
                        ></span>
                        <span>{status.label}</span>
                      </div>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}

            {/* Assignee Filter Dropdown - Multi-Select */}
            {uniqueAssignees.length > 0 && (
              <Dropdown className="ptt-assignee-filter-dropdown">
                <Dropdown.Toggle
                  variant="outline-secondary"
                  className="ptt-filter-toggle"
                >
                  <i className="fas fa-user me-1"></i>
                  {selectedAssignees.length === 0
                    ? "All Assignees"
                    : `${selectedAssignees.length} assignee${selectedAssignees.length > 1 ? "s" : ""} selected`}
                  {selectedAssignees.length > 0 && (
                    <Badge
                      bg="primary"
                      className="ms-2"
                      style={{ fontSize: "10px" }}
                    >
                      {selectedAssignees.length}
                    </Badge>
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu className="ptt-filter-menu ptt-multi-select-menu">
                  <Dropdown.Item
                    onClick={() => setSelectedAssignees([])}
                    className="ptt-select-all-item"
                  >
                    <i className="fas fa-times-circle me-2"></i> Clear All
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  {uniqueAssignees.map((assignee) => (
                    <Dropdown.Item
                      key={assignee.userId}
                      onClick={() => toggleAssignee(assignee.userId)}
                      active={selectedAssignees.includes(assignee.userId)}
                      className="ptt-multi-select-item"
                    >
                      <div className="d-flex align-items-center w-100">
                        <input
                          type="checkbox"
                          checked={selectedAssignees.includes(assignee.userId)}
                          onChange={() => {}}
                          className="me-2"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div
                          className="assignee-avatar-small"
                          style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            backgroundColor: assignee.avatarColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "10px",
                            fontWeight: "bold",
                            color: "white",
                            marginRight: "8px",
                          }}
                        >
                          {assignee.initials}
                        </div>
                        <span>{assignee.name}</span>
                      </div>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}

            {/* Search Box */}
            <div className="ptt-search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search tasks by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: "0 4px",
                  }}
                >
                  <i
                    className="fas fa-times"
                    style={{ fontSize: "10px", color: "#94a3b8" }}
                  ></i>
                </button>
              )}
            </div>

            {/* Export Button */}
            <button
              className="ptt-btn-primary"
              onClick={exportTasksToExcel}
              title="Export to Excel"
            >
              <i className="fas fa-download"></i> Export
            </button>
          </div>
        </div>

        {/* Active Filters Bar */}
        {isFilterActive() && (
          <div className="ptt-active-filters-bar">
            <span className="ptt-filters-label">
              <i className="fas fa-filter me-1"></i> Active Filters:
            </span>

            {selectedStatuses.map((statusValue) => {
              const status = uniqueStatuses.find(
                (s) => s.value === statusValue,
              );
              return (
                <span key={statusValue} className="ptt-filter-badge">
                  <i className="fas fa-tasks me-1"></i>
                  {status?.label || "Status"}
                  <button
                    onClick={() => toggleStatus(statusValue)}
                    className="ptt-filter-remove"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </span>
              );
            })}

            {selectedAssignees.map((assigneeId) => {
              const assignee = uniqueAssignees.find(
                (a) => a.userId === assigneeId,
              );
              return (
                <span key={assigneeId} className="ptt-filter-badge">
                  <i className="fas fa-user me-1"></i>
                  {assigneeId === "unassigned"
                    ? "Unassigned"
                    : assignee?.name || "Assignee"}
                  <button
                    onClick={() => toggleAssignee(assigneeId)}
                    className="ptt-filter-remove"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </span>
              );
            })}

            {searchQuery && (
              <span className="ptt-filter-badge">
                <i className="fas fa-search me-1"></i>"{searchQuery}"
                <button
                  onClick={() => setSearchQuery("")}
                  className="ptt-filter-remove"
                >
                  Remove
                </button>
              </span>
            )}

            <button onClick={clearFilters} className="ptt-clear-filters-btn">
              <i className="fas fa-trash-alt me-1"></i> Clear All
            </button>
          </div>
        )}

        {/* Loading State */}
        {tasksLoading && (
          <div className="ptt-loading">
            <div className="ptt-spinner"></div>
            <p>Loading tasks...</p>
          </div>
        )}

        {/* Tasks Content */}
        {!tasksLoading && (
          <div>
            {/* Render Stages */}
            {stages && stages.length > 0 && (
              <>
                {stages.map((stage) => {
                  const stageTasks = filteredStageTasks[stage.stageId] || [];

                  return (
                    <div key={stage.stageId} className="ptt-stage-section">
                      <div className="ptt-stage-header">
                        <div className="ptt-stage-title-wrapper">
                          <div className="ptt-stage-indicator"></div>
                          <span className="ptt-stage-title">
                            {stage.stageName}
                          </span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="ptt-stage-badge">
                            {stageTasks.length} tasks
                          </span>
                          {hasEditPermission && (
                            <>
                              <button
                                className="ptt-edit-stage-btn"
                                onClick={() => handleEditStage(stage)}
                                title="Edit Stage"
                              >
                                <i className="fas fa-edit"></i> Edit
                              </button>
                              {stageTasks.length === 0 && (
                                <button
                                  className="ptt-delete-stage-btn"
                                  onClick={() => handleDeleteStage(stage)}
                                  title="Delete Stage"
                                >
                                  <i className="fas fa-trash"></i> Delete
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {tasksLoadingByStage[stage.stageId] ? (
                        <div className="text-center py-3">
                          <div
                            className="ptt-spinner"
                            style={{ width: "24px", height: "24px" }}
                          ></div>
                        </div>
                      ) : (
                        renderTaskTable(stageTasks)
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {/* No Stage Section */}
            {noStageTasks.length > 0 && (
              <div className="ptt-stage-section ptt-no-stage-section">
                <div className="ptt-stage-header">
                  <div className="ptt-stage-title-wrapper">
                    <div
                      className="ptt-stage-indicator"
                      style={{ backgroundColor: "#94a3b8" }}
                    ></div>
                    <span className="ptt-stage-title">No Stage Assigned</span>
                  </div>
                  <div>
                    <span className="ptt-stage-badge">
                      {noStageTasks.length} tasks
                    </span>
                  </div>
                </div>
                {renderTaskTable(noStageTasks)}
              </div>
            )}

            {/* Empty State */}
            {!hasAnyTasks() && (
              <div className="ptt-empty-state">
                <i className="fas fa-tasks"></i>
                <p>No tasks found for this project.</p>
                {hasEditPermission && (
                  <button
                    className="ptt-btn-primary mt-2"
                    onClick={() => setShowTaskOptions(true)}
                  >
                    Create First Task
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectTasksTab;

// import React, { useEffect, useState } from "react";
// import { Badge, OverlayTrigger, Tooltip, Dropdown } from "react-bootstrap";
// import moment from "moment";
// import { getContactNames } from "../../Api/TicketApiOrg";
// import "./ProjectTasksTab.css";

// const ProjectTasksTab = ({
//   // Data
//   stages,
//   filteredStageTasks,
//   tasksLoading,
//   tasksLoadingByStage,
//   taskAssignmentsByTask,
//   teamMemberDetails,
//   resourcesByOrg,
//   clientContactPersons,
//   partnerContactPersons,
//   contactNamesCache,
//   setContactNamesCache,
//   ticketNameMap,
//   eventNameMap,
//   taskBuildingBlocks,
//   taskTypesLoading,
//   taskStatusesLoading,
//   handleBatchCreateClick,

//   // Permissions
//   hasEditPermission,

//   // Handlers
//   handleOpenTaskDetails,
//   handleEditTask,
//   handleSoftDeleteTask,
//   openTaskDialog,
//   getCurrentAssignedUsersForTask,
//   getTaskTypeName,
//   getTaskStatusName,
//   getPriorityBadge,
//   getStatusBadge,
//   handleEditStage,
//   handleCreateStage,
//   setShowTaskOptions,
//   showTaskOptions,
//   handleExcelUploadClick,
//   handleManualTaskClick,
//   exportTasksToExcel,
//   searchQuery,
//   setSearchQuery,
//   projectId,
// }) => {
//   // State for hover tooltip
//   const [hoveredAssignee, setHoveredAssignee] = useState(null);

//   // Sorting state
//   const [sortConfig, setSortConfig] = useState({
//     key: null, // 'taskId' or 'endDate'
//     direction: "asc", // 'asc' or 'desc'
//   });

//   // Filter state
//   const [selectedAssignee, setSelectedAssignee] = useState("all");
//   const [uniqueAssignees, setUniqueAssignees] = useState([]);

//   // Function to fetch contact names for GUIDs
//   const fetchContactNames = async (guids) => {
//     if (!guids || guids.length === 0) return;

//     const validGuids = guids.filter(
//       (g) =>
//         g &&
//         /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
//           g,
//         ),
//     );

//     if (validGuids.length === 0) return;

//     try {
//       const res = await getContactNames(validGuids);
//       const map = {};
//       res.forEach((c) => {
//         map[c.contactGuid.toLowerCase()] = c.contactName;
//       });
//       setContactNamesCache((prev) => ({
//         ...prev,
//         ...map,
//       }));
//     } catch (err) {
//       console.error("Failed to fetch contact names", err);
//     }
//   };

//   // Collect all GUIDs from assignments and fetch their names
//   useEffect(() => {
//     const allGuids = new Set();

//     // Get all tasks from filteredStageTasks
//     const allTasks = Object.values(filteredStageTasks).flat();

//     allTasks.forEach((task) => {
//       const assignments = taskAssignmentsByTask[task.taskId] || [];
//       assignments.forEach((assignment) => {
//         const userId = assignment.assignedUserId;
//         if (
//           userId &&
//           /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
//             userId,
//           )
//         ) {
//           if (!contactNamesCache[userId.toLowerCase()]) {
//             allGuids.add(userId);
//           }
//         }
//       });
//     });

//     if (allGuids.size > 0) {
//       fetchContactNames(Array.from(allGuids));
//     }
//   }, [filteredStageTasks, taskAssignmentsByTask]);

//   // Extract unique assignees from all tasks
//   useEffect(() => {
//     const allTasks = Object.values(filteredStageTasks).flat();
//     const assigneeMap = new Map();

//     allTasks.forEach((task) => {
//       const assignments = taskAssignmentsByTask[task.taskId] || [];
//       assignments.forEach((assignment) => {
//         const userId = assignment.assignedUserId;
//         if (userId) {
//           const assigneeName = getAssigneeName(userId);
//           if (!assigneeMap.has(userId)) {
//             assigneeMap.set(userId, {
//               userId: userId,
//               name: assigneeName,
//               avatarColor: getAvatarColor(userId),
//               initials: getInitials(assigneeName),
//             });
//           }
//         }
//       });
//     });

//     // Add "Unassigned" filter option
//     const hasUnassignedTasks = allTasks.some((task) => {
//       const assignments = taskAssignmentsByTask[task.taskId] || [];
//       return assignments.length === 0;
//     });

//     const assigneeList = Array.from(assigneeMap.values()).sort((a, b) =>
//       a.name.localeCompare(b.name),
//     );

//     if (hasUnassignedTasks) {
//       assigneeList.unshift({
//         userId: "unassigned",
//         name: "Unassigned",
//         avatarColor: "#94a3b8",
//         initials: "??",
//       });
//     }

//     setUniqueAssignees(assigneeList);
//   }, [filteredStageTasks, taskAssignmentsByTask, contactNamesCache]);

//   // Helper function to sort tasks
//   const sortTasks = (tasks) => {
//     if (!sortConfig.key || !tasks) return tasks;

//     const sortedTasks = [...tasks];

//     sortedTasks.sort((a, b) => {
//       let aValue, bValue;

//       if (sortConfig.key === "taskId") {
//         aValue = extractTaskIdNumber(a.taskId);
//         bValue = extractTaskIdNumber(b.taskId);
//       } else if (sortConfig.key === "endDate") {
//         aValue = a.taskEndDate ? new Date(a.taskEndDate).getTime() : null;
//         bValue = b.taskEndDate ? new Date(b.taskEndDate).getTime() : null;

//         if (aValue === null && bValue === null) return 0;
//         if (aValue === null) return 1;
//         if (bValue === null) return -1;
//       } else {
//         return 0;
//       }

//       if (sortConfig.direction === "asc") {
//         return aValue > bValue ? 1 : -1;
//       } else {
//         return aValue < bValue ? 1 : -1;
//       }
//     });

//     return sortedTasks;
//   };

//   // Helper function to filter tasks by assignee
//   const filterTasksByAssignee = (tasks) => {
//     if (!selectedAssignee || selectedAssignee === "all") return tasks;

//     return tasks.filter((task) => {
//       const assignments = taskAssignmentsByTask[task.taskId] || [];

//       if (selectedAssignee === "unassigned") {
//         return assignments.length === 0;
//       }

//       return assignments.some(
//         (assignment) => assignment.assignedUserId === selectedAssignee,
//       );
//     });
//   };

//   // Helper function to extract numeric value from Task ID
//   const extractTaskIdNumber = (taskId) => {
//     if (!taskId) return 0;
//     const match = taskId.match(/\d+$/);
//     if (match) {
//       return parseInt(match[0], 10);
//     }
//     return taskId;
//   };

//   // Handle sort click
//   const handleSort = (key) => {
//     setSortConfig((prevConfig) => ({
//       key,
//       direction:
//         prevConfig.key === key && prevConfig.direction === "asc"
//           ? "desc"
//           : "asc",
//     }));
//   };

//   // Get sort icon
//   const getSortIcon = (key) => {
//     if (sortConfig.key !== key) {
//       return (
//         <i
//           className="fas fa-sort ms-1"
//           style={{ fontSize: "12px", opacity: 0.5 }}
//         ></i>
//       );
//     }
//     return sortConfig.direction === "asc" ? (
//       <i className="fas fa-sort-up ms-1" style={{ fontSize: "12px" }}></i>
//     ) : (
//       <i className="fas fa-sort-down ms-1" style={{ fontSize: "12px" }}></i>
//     );
//   };

//   // Clear all filters
//   const clearFilters = () => {
//     setSelectedAssignee("all");
//     setSearchQuery("");
//   };

//   // Helper function to get ALL assignments for a task
//   const getAllAssignmentsForTask = (taskId) => {
//     return taskAssignmentsByTask[taskId] || [];
//   };

//   // Helper function to get assignee name
//   const getAssigneeName = (userId) => {
//     if (!userId) return "Unknown";

//     const contactName = contactNamesCache?.[userId?.toLowerCase()];
//     if (contactName) return contactName;

//     const teamMember = teamMemberDetails?.find(
//       (user) => String(user.userId) === String(userId),
//     );
//     if (teamMember) return teamMember.username;

//     const clientContact = clientContactPersons?.find(
//       (c) => c.cusVenId === userId || c.contactGuid === userId,
//     );
//     if (clientContact)
//       return clientContact.contactPerName || clientContact.contactName;

//     const partnerContact = partnerContactPersons?.find(
//       (c) => c.cusVenId === userId || c.contactGuid === userId,
//     );
//     if (partnerContact)
//       return partnerContact.contactPerName || partnerContact.contactName;

//     const resource = resourcesByOrg?.find(
//       (res) => String(res.resourceId) === String(userId),
//     );
//     if (resource) return resource.name;

//     const isGuid =
//       /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
//         userId,
//       );
//     if (isGuid) return userId.substring(0, 8);

//     return userId;
//   };

//   // Helper function to get initials
//   const getInitials = (name) => {
//     if (!name || name === "Unknown") return "??";

//     const cleanName = name.trim();

//     if (cleanName.length === 2) return cleanName.toUpperCase();

//     const words = cleanName.split(/\s+/);
//     if (words.length >= 2) {
//       const firstInitial = words[0][0]?.toUpperCase() || "";
//       const lastInitial = words[words.length - 1][0]?.toUpperCase() || "";
//       return firstInitial + lastInitial;
//     }

//     if (cleanName.length >= 2) {
//       return cleanName.substring(0, 2).toUpperCase();
//     }

//     if (cleanName.length === 1) {
//       return cleanName[0].toUpperCase() + "?";
//     }

//     return "??";
//   };

//   // Helper function to get avatar color
//   const getAvatarColor = (userId) => {
//     const colors = [
//       "#6366f1",
//       "#8b5cf6",
//       "#ec4899",
//       "#f43f5e",
//       "#ef4444",
//       "#f97316",
//       "#f59e0b",
//       "#eab308",
//       "#84cc16",
//       "#10b981",
//       "#14b8a6",
//       "#06b6d4",
//       "#3b82f6",
//       "#6b7280",
//     ];
//     let hash = 0;
//     const str = String(userId);
//     for (let i = 0; i < str.length; i++) {
//       hash = (hash << 5) - hash + str.charCodeAt(i);
//       hash |= 0;
//     }
//     return colors[Math.abs(hash) % colors.length];
//   };

//   // Helper to get priority display
//   const getPriorityDisplay = (priority) => {
//     if (priority === "Critical" || priority === "critical") {
//       return (
//         <div className="ptt-priority-critical">
//           <i className="fas fa-exclamation-triangle"></i>
//           <span>Critical</span>
//         </div>
//       );
//     }
//     if (priority === "High" || priority === "high") {
//       return (
//         <div className="ptt-priority-high">
//           <i className="fas fa-flag"></i>
//           <span>High</span>
//         </div>
//       );
//     }
//     if (priority === "Medium" || priority === "medium") {
//       return (
//         <div className="ptt-priority-medium">
//           <i className="fas fa-flag"></i>
//           <span>Medium</span>
//         </div>
//       );
//     }
//     if (priority === "Low" || priority === "low") {
//       return (
//         <div className="ptt-priority-low">
//           <i className="fas fa-flag"></i>
//           <span>Low</span>
//         </div>
//       );
//     }
//     return (
//       <div className="ptt-priority-default">
//         <i className="fas fa-minus-circle"></i>
//         <span>{priority || "No Priority"}</span>
//       </div>
//     );
//   };

//   // Helper to get status display
//   const getStatusDisplay = (status) => {
//     const statusName = getTaskStatusName(status);
//     if (statusName === "Completed") {
//       return <span className="ptt-status-completed">{statusName}</span>;
//     }
//     if (statusName === "In Progress" || statusName === "Active") {
//       return <span className="ptt-status-active">{statusName}</span>;
//     }
//     return <span className="ptt-status-pending">{statusName}</span>;
//   };

//   // Helper to check if there are any tasks at all
//   const hasAnyTasks = () => {
//     return Object.values(filteredStageTasks).some(
//       (tasks) => tasks && tasks.length > 0,
//     );
//   };

//   // Check if any filter is active
//   const isFilterActive = () => {
//     return selectedAssignee !== "all" || searchQuery !== "";
//   };

//   // Get active filter count
//   const getActiveFilterCount = () => {
//     let count = 0;
//     if (selectedAssignee !== "all") count++;
//     if (searchQuery) count++;
//     return count;
//   };

//   const noStageTasks = filteredStageTasks["no-stage"] || [];

//   // Render tasks table component (reused for both stages and no-stage)
//   const renderTaskTable = (tasks, showStageColumn = false) => {
//     // First filter by assignee, then by search query, then sort
//     let filteredTasks = filterTasksByAssignee(tasks);

//     // Filter by search query
//     if (searchQuery) {
//       filteredTasks = filteredTasks.filter((task) =>
//         task.taskName?.toLowerCase().includes(searchQuery.toLowerCase()),
//       );
//     }

//     const sortedTasks = sortTasks(filteredTasks);

//     return (
//       <div className="table-responsive">
//         <table className="ptt-task-table">
//           <thead>
//             <tr>
//               <th
//                 style={{ width: "7%", cursor: "pointer" }}
//                 onClick={() => handleSort("taskId")}
//               >
//                 TaskId {getSortIcon("taskId")}
//               </th>
//               <th className="ptt-col-name">Name</th>
//               <th className="ptt-col-assignee">Assignee</th>
//               <th
//                 style={{ cursor: "pointer" }}
//                 onClick={() => handleSort("endDate")}
//               >
//                 End Date {getSortIcon("endDate")}
//               </th>
//               <th className="ptt-col-priority">Priority</th>
//               <th className="ptt-col-status">Status</th>
//               <th className="ptt-col-actions">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {sortedTasks.map((task) => {
//               const allAssignments = getAllAssignmentsForTask(task.taskId);

//               return (
//                 <tr key={task.taskId} className="ptt-task-row">
//                   <td className="ptt-task-id-cell">{task.taskId}</td>

//                   <td className="ptt-col-name">
//                     <div className="ptt-task-name">
//                       <i className="fas fa-play ptt-play-icon"></i>
//                       <i className="fas fa-check-circle ptt-check-icon"></i>
//                       <a
//                         href={`/project/${task.projectBelong || projectId}/task/${task.taskId}`}
//                         className="ptt-task-name-text"
//                         onClick={(e) => {
//                           if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
//                             e.preventDefault();
//                             handleOpenTaskDetails(task.taskId);
//                           }
//                         }}
//                         title="Click to view task details (Ctrl+Click to open in new tab)"
//                       >
//                         {task.taskName}
//                       </a>
//                     </div>
//                   </td>

//                   <td className="ptt-col-assignee">
//                     {allAssignments.length === 0 ? (
//                       <div className="d-flex align-items-center gap-2">
//                         <span className="ptt-unassigned-text">Unassigned</span>
//                         {hasEditPermission && (
//                           <button
//                             className="ptt-assign-btn"
//                             onClick={() => openTaskDialog(task)}
//                             title="Manage Assignments"
//                           >
//                             <i className="fas fa-user-plus"></i>
//                           </button>
//                         )}
//                       </div>
//                     ) : (
//                       <div className="ptt-assignee-avatars-container">
//                         {allAssignments.map((assignment, idx) => {
//                           const assigneeName = getAssigneeName(
//                             assignment.assignedUserId,
//                           );
//                           const initials = getInitials(assigneeName);
//                           const avatarColor = getAvatarColor(
//                             assignment.assignedUserId,
//                           );

//                           return (
//                             <OverlayTrigger
//                               key={assignment.assignmentId || idx}
//                               placement="top"
//                               overlay={
//                                 <Tooltip
//                                   id={`tooltip-${assignment.assignmentId || idx}`}
//                                 >
//                                   <div className="text-center">
//                                     <strong>{assigneeName}</strong>
//                                   </div>
//                                 </Tooltip>
//                               }
//                             >
//                               <div
//                                 className="ptt-assignee-avatar"
//                                 style={{ backgroundColor: avatarColor }}
//                                 title={assigneeName}
//                               >
//                                 {initials}
//                               </div>
//                             </OverlayTrigger>
//                           );
//                         })}
//                         {hasEditPermission && (
//                           <button
//                             className="ptt-assign-btn-icon"
//                             onClick={() => openTaskDialog(task)}
//                             title="Manage Assignments"
//                           >
//                             <i className="fas fa-edit"></i>
//                           </button>
//                         )}
//                       </div>
//                     )}
//                   </td>

//                   <td className="ptt-date-cell">
//                     {task.taskEndDate
//                       ? moment(task.taskEndDate).format("MMM DD, YYYY")
//                       : "-"}
//                   </td>

//                   <td className="text-center">
//                     {getPriorityDisplay(task.taskPriority || "No Priority")}
//                   </td>

//                   <td className="text-center">
//                     {getStatusDisplay(task.taskStatus)}
//                   </td>

//                   <td className="ptt-actions-cell">
//                     {hasEditPermission && (
//                       <button
//                         className="ptt-delete-task-btn"
//                         onClick={() => handleSoftDeleteTask(task.taskId)}
//                         title="Delete Task"
//                       >
//                         Remove
//                       </button>
//                     )}
//                   </td>
//                 </tr>
//               );
//             })}
//             {sortedTasks.length === 0 && (
//               <tr>
//                 <td colSpan="6" className="text-center py-3 text-muted">
//                   No tasks found matching the filters
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>
//     );
//   };

//   return (
//     <div className="ptt-tasks-container">
//       <div className="ptt-task-list-container">
//         {/* Controls Bar */}
//         <div className="ptt-controls-bar">
//           <div className="ptt-left-controls">
//             {hasEditPermission && (
//               <>
//                 <div className="ptt-dropdown">
//                   <button
//                     className="ptt-btn-primary"
//                     onClick={() => setShowTaskOptions(!showTaskOptions)}
//                   >
//                     Add Task
//                   </button>
//                   {showTaskOptions && (
//                     <>
//                       <div
//                         className="ptt-overlay"
//                         onClick={() => setShowTaskOptions(false)}
//                       />
//                       <div className="ptt-dropdown-menu">
//                         <div
//                           className="ptt-dropdown-item"
//                           onClick={handleBatchCreateClick}
//                         >
//                           <i className="fas fa-layer-group me-2"></i>
//                           <div>
//                             <span>Bulk Create Tasks</span>
//                             <small
//                               className="text-muted d-block"
//                               style={{ fontSize: "10px" }}
//                             >
//                               Create multiple tasks at once
//                             </small>
//                           </div>
//                         </div>

//                         <div
//                           className="ptt-dropdown-item"
//                           onClick={handleExcelUploadClick}
//                         >
//                           <i className="fas fa-file-excel me-2 text-success"></i>
//                           <div>
//                             <span>Import from Excel</span>
//                             <small
//                               className="text-muted d-block"
//                               style={{ fontSize: "10px" }}
//                             >
//                               Upload Excel file with tasks
//                             </small>
//                           </div>
//                         </div>

//                         <div
//                           className="ptt-dropdown-item"
//                           onClick={handleManualTaskClick}
//                         >
//                           <i className="fas fa-plus-circle me-2 text-primary"></i>
//                           <div>
//                             <span>Create Single Task</span>
//                             <small
//                               className="text-muted d-block"
//                               style={{ fontSize: "10px" }}
//                             >
//                               Add one task manually
//                             </small>
//                           </div>
//                         </div>
//                       </div>
//                     </>
//                   )}
//                 </div>

//                 <button className="ptt-btn-primary" onClick={handleCreateStage}>
//                   Add Stage
//                 </button>
//               </>
//             )}
//           </div>

//           <div className="ptt-right-controls">
//             {/* Assignee Filter Dropdown */}
//             {uniqueAssignees.length > 0 && (
//               <Dropdown className="ptt-assignee-filter-dropdown">
//                 <Dropdown.Toggle
//                   variant="outline-secondary"
//                   className="ptt-filter-toggle"
//                 >
//                   <i className="fas fa-user me-1"></i>
//                   {selectedAssignee === "all"
//                     ? "All Assignees"
//                     : selectedAssignee === "unassigned"
//                       ? "Unassigned"
//                       : uniqueAssignees.find(
//                           (a) => a.userId === selectedAssignee,
//                         )?.name || "Assignee"}
//                   {getActiveFilterCount() > 0 && (
//                     <Badge
//                       bg="primary"
//                       className="ms-2"
//                       style={{ fontSize: "10px" }}
//                     >
//                       {getActiveFilterCount()}
//                     </Badge>
//                   )}
//                 </Dropdown.Toggle>
//                 <Dropdown.Menu className="ptt-filter-menu">
//                   <Dropdown.Item
//                     onClick={() => setSelectedAssignee("all")}
//                     active={selectedAssignee === "all"}
//                   >
//                     <i className="fas fa-users me-2"></i> All Assignees
//                   </Dropdown.Item>
//                   <Dropdown.Divider />
//                   {uniqueAssignees.map((assignee) => (
//                     <Dropdown.Item
//                       key={assignee.userId}
//                       onClick={() => setSelectedAssignee(assignee.userId)}
//                       active={selectedAssignee === assignee.userId}
//                     >
//                       <div className="d-flex align-items-center">
//                         <div
//                           className="assignee-avatar-small"
//                           style={{
//                             width: "24px",
//                             height: "24px",
//                             borderRadius: "50%",
//                             backgroundColor: assignee.avatarColor,
//                             display: "flex",
//                             alignItems: "center",
//                             justifyContent: "center",
//                             fontSize: "10px",
//                             fontWeight: "bold",
//                             color: "white",
//                             marginRight: "8px",
//                           }}
//                         >
//                           {assignee.initials}
//                         </div>
//                         <span>{assignee.name}</span>
//                       </div>
//                     </Dropdown.Item>
//                   ))}
//                   {(selectedAssignee !== "all" || searchQuery) && (
//                     <>
//                       <Dropdown.Divider />
//                       <Dropdown.Item onClick={clearFilters}>
//                         <i className="fas fa-times-circle me-2"></i> Clear
//                         Filters
//                       </Dropdown.Item>
//                     </>
//                   )}
//                 </Dropdown.Menu>
//               </Dropdown>
//             )}

//             {/* Search Box */}
//             <div className="ptt-search-box">
//               <i className="fas fa-search"></i>
//               <input
//                 type="text"
//                 placeholder="Search tasks by name..."
//                 value={searchQuery}
//                 onChange={(e) => setSearchQuery(e.target.value)}
//               />
//               {searchQuery && (
//                 <button
//                   onClick={() => setSearchQuery("")}
//                   style={{
//                     background: "transparent",
//                     border: "none",
//                     padding: "0 4px",
//                   }}
//                 >
//                   <i
//                     className="fas fa-times"
//                     style={{ fontSize: "10px", color: "#94a3b8" }}
//                   ></i>
//                 </button>
//               )}
//             </div>

//             {/* Export Button */}
//             <button
//               className="ptt-btn-primary"
//               onClick={exportTasksToExcel}
//               title="Export to Excel"
//             >
//               <i className="fas fa-download"></i> Export
//             </button>
//           </div>
//         </div>

//         {/* Active Filters Bar */}
//         {isFilterActive() && (
//           <div className="ptt-active-filters-bar">
//             <span className="ptt-filters-label">
//               <i className="fas fa-filter me-1"></i> Active Filters:
//             </span>
//             {selectedAssignee !== "all" && (
//               <span className="ptt-filter-badge">
//                 <i className="fas fa-user me-1"></i>
//                 {selectedAssignee === "unassigned"
//                   ? "Unassigned"
//                   : uniqueAssignees.find((a) => a.userId === selectedAssignee)
//                       ?.name || "Assignee"}
//                 <button
//                   onClick={() => setSelectedAssignee("all")}
//                   className="ptt-filter-remove"
//                 >
//                   <i className="fas fa-times"></i>
//                 </button>
//               </span>
//             )}
//             {searchQuery && (
//               <span className="ptt-filter-badge">
//                 <i className="fas fa-search me-1"></i>"{searchQuery}"
//                 <button
//                   onClick={() => setSearchQuery("")}
//                   className="ptt-filter-remove"
//                 >
//                   <i className="fas fa-times"></i>
//                 </button>
//               </span>
//             )}
//             <button onClick={clearFilters} className="ptt-clear-filters-btn">
//               <i className="fas fa-trash-alt me-1"></i> Clear All
//             </button>
//           </div>
//         )}

//         {/* Loading State */}
//         {tasksLoading && (
//           <div className="ptt-loading">
//             <div className="ptt-spinner"></div>
//             <p>Loading tasks...</p>
//           </div>
//         )}

//         {/* Tasks Content */}
//         {!tasksLoading && (
//           <div>
//             {/* Render Stages */}
//             {stages && stages.length > 0 && (
//               <>
//                 {stages.map((stage) => {
//                   const stageTasks = filteredStageTasks[stage.stageId] || [];

//                   return (
//                     <div key={stage.stageId} className="ptt-stage-section">
//                       <div className="ptt-stage-header">
//                         <div className="ptt-stage-title-wrapper">
//                           <div className="ptt-stage-indicator"></div>
//                           <span className="ptt-stage-title">
//                             {stage.stageName}
//                           </span>
//                         </div>
//                         <div className="d-flex align-items-center gap-2">
//                           <span className="ptt-stage-badge">
//                             {stageTasks.length} tasks
//                           </span>
//                           {hasEditPermission && (
//                             <button
//                               className="ptt-edit-stage-btn"
//                               onClick={() => handleEditStage(stage)}
//                               title="Edit Stage"
//                             >
//                               <i className="fas fa-edit"></i> Edit
//                             </button>
//                           )}
//                         </div>
//                       </div>

//                       {tasksLoadingByStage[stage.stageId] ? (
//                         <div className="text-center py-3">
//                           <div
//                             className="ptt-spinner"
//                             style={{ width: "24px", height: "24px" }}
//                           ></div>
//                         </div>
//                       ) : (
//                         renderTaskTable(stageTasks)
//                       )}
//                     </div>
//                   );
//                 })}
//               </>
//             )}

//             {/* No Stage Section */}
//             {noStageTasks.length > 0 && (
//               <div className="ptt-stage-section ptt-no-stage-section">
//                 <div className="ptt-stage-header">
//                   <div className="ptt-stage-title-wrapper">
//                     <div
//                       className="ptt-stage-indicator"
//                       style={{ backgroundColor: "#94a3b8" }}
//                     ></div>
//                     <span className="ptt-stage-title">No Stage Assigned</span>
//                   </div>
//                   <div>
//                     <span className="ptt-stage-badge">
//                       {noStageTasks.length} tasks
//                     </span>
//                   </div>
//                 </div>
//                 {renderTaskTable(noStageTasks)}
//               </div>
//             )}

//             {/* Empty State */}
//             {!hasAnyTasks() && (
//               <div className="ptt-empty-state">
//                 <i className="fas fa-tasks"></i>
//                 <p>No tasks found for this project.</p>
//                 {hasEditPermission && (
//                   <button
//                     className="ptt-btn-primary mt-2"
//                     onClick={() => setShowTaskOptions(true)}
//                   >
//                     Create First Task
//                   </button>
//                 )}
//               </div>
//             )}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ProjectTasksTab;
